<template>
  <div class="start-page-bg">
    <div class="container">
      <div class="row align-items-center justify-content-center vh-100">
        
        <div v-if="isLoading" class="text-center text-light">
          <div class="spinner-border" role="status"></div>
          <p class="mt-2">Checking contract status...</p>
        </div>

        <div v-else class="card col-xl-5 col-md-8 col-lg-6 col-sm-12" style="top: 5%">
          <div class="card-body">
            <div class="text-center justify-content-center mb-4">
              <img src="@/assets/img/vertex_logo.png" alt="vertex_logo" class="img-fluid w-50"
                onerror="this.onerror=null; this.src='https://placehold.co/200x100?text=Vertex+Logo';" />
            </div>

            <div v-if="alreadyDeclined" class="text-center py-4">
              <h3 class="card-title"><strong>Contract Declined</strong></h3>
              <div class="m-3">
                <h6 class="card-subtitle">
                  This contract has been declined. <br><br>
                  Thank you for your feedback.
                </h6>
              </div>
            </div>

            <div v-else-if="submitted" class="text-center py-4">
              <h3 class="card-title"><strong>Thank You!</strong></h3>
              <div class="m-3">
                <h6 class="card-subtitle">
                  Thank you for helping us improve our service.
                </h6>
              </div>
            </div>

            <div v-else>
              <div class="text-center justify-content-center">
                <h3 class="card-title"><strong>Contract Feedback</strong></h3>
              </div>

              <div class="m-3 text-center">
                <h6 class="card-subtitle">
                  We are sorry to hear you won't be proceeding with this contract.
                  <br><br>
                  We'd appreciate your feedback to help us improve.
                </h6>
              </div>

              <hr />

              <form ref="feedbackFormRef" class="row mx-4 g-0" style="gap: 1.5rem" @submit.prevent="submitFeedback">
                
                <div class="form-group">
                  <label for="declineReason" class="form-label">
                    Why did you choose not to proceed with this contract?
                    <span class="requiredField">*</span>
                  </label>
                  <VueMultiselect 
                    v-model="feedback.reasons" 
                    :options="declineReasons" 
                    :multiple="true"
                    :close-on-select="false" 
                    placeholder="Select one or more reasons" 
                    label="text" 
                    track-by="value"
                    :class="{ 'please-select-color': feedback.reasons.length > 0 }"
                    @blur="v$.feedback.reasons.$touch">
                  </VueMultiselect>
                  <span class="form-text text-danger error-msg" v-if="v$.feedback.reasons.$error">
                    Please select at least one reason.
                  </span>
                </div>

                <div v-if="isReasonSelected('alternate_provider')" class="form-floating">
                  <input type="text" id="alternateProvider" v-model.trim="feedback.alternateProvider"
                    class="form-control" placeholder="Alternate Provider Name" />
                  <label for="alternateProvider" class="text-muted">Alternate Provider Name</label>
                </div>

                <div v-if="isReasonSelected('other')" class="form-floating">
                  <input type="text" id="otherReason" v-model.trim="feedback.otherReasonDetails"
                    class="form-control" placeholder="Please specify" />
                  <label for="otherReason" class="text-muted">Other Reason Details</label>
                </div>

                <div class="form-floating">
                  <textarea class="form-control" id="otherFeedback" v-model.trim="feedback.otherFeedback"
                    placeholder="Please provide any other feedback" style="height: 150px"></textarea>
                  <label for="otherFeedback" class="text-muted">Other feedback on your experience</label>
                </div>

                <div class="mb-3 text-center">
                  <p v-if="v$.$error" class="form-text text-danger">
                    Please review and fill up all required fields.
                  </p>
                  <button type="submit" class="btn btn-info text-light px-5" :disabled="isProcessing">
                    {{ isProcessing ? "Submitting..." : "Submit Feedback" }}
                  </button>
                </div>
              </form>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import VueMultiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";

export default {
  name: "ContractFeedback",
  components: {
    VueMultiselect,
  },
  props: {
    unique_key: { type: String, required: true },
    access_key: { type: String, required: true },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isLoading: true, // New loading state
      isProcessing: false,
      submitted: false,
      alreadyDeclined: false, // For checking existing decline status
      feedback: {
        reasons: [],
        alternateProvider: "",
        otherReasonDetails: "",
        otherFeedback: "",
      },
      declineReasons: [
        { text: "No longer required product or service", value: "no_longer_required" },
        { text: "Went with an alternate provider", value: "alternate_provider" },
        { text: "Too expensive", value: "too_expensive" },
        { text: "Quality or reputation concerns", value: "quality_reputation_concerns" },
        { text: "Internal reasons", value: "internal_reasons" },
        { text: "Other", value: "other" },
      ],
    };
  },
  validations() {
    return {
      feedback: {
        reasons: { required },
      },
    };
  },
  async created() {
    // Check status as soon as component is initialized
    await this.getContractDetails();
  },
  methods: {
    async getContractDetails() {
      this.isLoading = true;
      try {
        const response = await this.$axios_internal.get(`/contract/view-contract/${this.unique_key}/${this.access_key}`);
        const contract = response.data.vertex_contract;

        console.log(contract)

        if (!contract) {
          this.$router.push({ name: 'ContractDeletedNonExist' });
          return;
        }

        // Logic check: If status is already 4 (Declined)
        if (contract.contract_feedback != null) {
          this.alreadyDeclined = true;
        }
      } catch (error) {
        console.error("Error fetching details:", error);
        this.$router.push({ name: 'ContractDeletedNonExist' });
      } finally {
        this.isLoading = false;
      }
    },

    isReasonSelected(reasonValue) {
      return this.feedback.reasons.some((reason) => reason.value === reasonValue);
    },

    async submitFeedback() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;

      this.isProcessing = true;

      const feedbackPromises = this.feedback.reasons.map(reason => {
        let extraField = "";
        if (reason.value === 'alternate_provider') {
          extraField = this.feedback.alternateProvider;
        } else if (reason.value === 'other') {
          extraField = this.feedback.otherReasonDetails;
        }

        return this.$axios_internal.post('/contracts/feedback', {
          unique_key: this.unique_key,
          access_key: this.access_key,
          feedback: this.feedback.otherFeedback,
          chosen_feedback: reason.value,
          chosen_feedback_extra_field: extraField,
        });
      });

      try {
        await Promise.all(feedbackPromises);
        this.submitted = true;
      } catch (error) {
        this.$toast.error("There was an error submitting your feedback.");
      } finally {
        this.isProcessing = false;
      }
    },
  },
};
</script>

<style scoped>
.requiredField {
  color: red;
}

/* Target the internal tags container when our custom class is applied.
   This changes the background color of the input box when items are selected.
*/
/* ::v-deep .please-select-color .multiselect__tags {
  background: #00a0c1 !important;
  border-color: #00a0c1 !important;
} */

/* Change text and placeholder colors inside the blue box */
::v-deep .please-select-color .multiselect__placeholder,
::v-deep .please-select-color .multiselect__single {
  color: #fff !important;
}

/* Fix for the blue hover and "Press enter to select" background */
::v-deep .multiselect__option--highlight {
  background: #00a0c1 !important;
  color: #fff !important;
}

::v-deep .multiselect__option--highlight::after {
  background: #00a0c1 !important;
  color: #fff !important;
}
</style>
